/**
 * CreditPurchaseModal
 * Modal for selecting and purchasing AI credit packs via Stripe Checkout
 */

import React, { useState, useEffect } from 'react';
import { Modal, Button, Card, Row, Col, Spinner, Badge } from 'react-bootstrap';
import { Zap, CreditCard } from 'lucide-react';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import { toast } from 'sonner';

const CreditPurchaseModal = ({ show, onHide, companyId }) => {
    const [creditPacks, setCreditPacks] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [checkoutLoading, setCheckoutLoading] = useState(null);

    useEffect(() => {
        if (show) {
            fetchCreditPacks();
        }
    }, [show]);

    const fetchCreditPacks = async () => {
        try {
            setIsLoading(true);
            const res = await axios.get(getApiUrl('subscription/credit-packs'), { withCredentials: true });
            setCreditPacks(res.data || []);
        } catch (error) {
            console.error('Error fetching credit packs:', error);
            toast.error('Kunde inte hämta kreditpaket');
        } finally {
            setIsLoading(false);
        }
    };

    const handlePurchase = async (packId) => {
        try {
            setCheckoutLoading(packId);
            const res = await axios.post(
                getApiUrl(`${companyId}/subscription/credits/checkout`),
                { packId },
                { withCredentials: true }
            );

            if (res.data?.url) {
                window.location.href = res.data.url;
            } else {
                toast.error('Kunde inte skapa betalningssession');
            }
        } catch (error) {
            console.error('Error creating checkout session:', error);
            if (error.response?.status === 503) {
                toast.error('Betalning är inte konfigurerad');
            } else {
                toast.error('Kunde inte starta betalning');
            }
            setCheckoutLoading(null);
        }
    };

    const getPackVariant = (index) => {
        const variants = ['light', 'info', 'primary', 'success'];
        return variants[index] || 'light';
    };

    return (
        <Modal show={show} onHide={onHide} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title className="d-flex align-items-center">
                    <Zap size={20} className="me-2 text-primary" />
                    Köp AI-krediter
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {isLoading ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" variant="primary" />
                        <p className="text-muted mt-2">Laddar kreditpaket...</p>
                    </div>
                ) : (
                    <>
                        <p className="text-muted mb-4">
                            Välj ett kreditpaket nedan. Du kommer att omdirigeras till en säker betalningssida.
                        </p>
                        <Row>
                            {creditPacks.map((pack, index) => (
                                <Col md={6} lg={3} key={pack.id} className="mb-3">
                                    <Card
                                        className={`h-100 text-center ${index === 2 ? 'border-primary' : ''}`}
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => !checkoutLoading && handlePurchase(pack.id)}
                                    >
                                        {index === 2 && (
                                            <div className="position-absolute top-0 start-50 translate-middle">
                                                <Badge bg="primary" className="px-2 py-1" style={{ fontSize: '0.7rem' }}>
                                                    Populär
                                                </Badge>
                                            </div>
                                        )}
                                        <Card.Body className="d-flex flex-column pt-4">
                                            <h6 className="text-muted mb-1">{pack.label}</h6>
                                            <h2 className="mb-0" style={{ color: 'var(--bs-primary)' }}>
                                                {pack.credits}
                                            </h2>
                                            <small className="text-muted mb-3">krediter</small>

                                            <div className="mt-auto">
                                                <h4 className="mb-1">{pack.priceSek} kr</h4>
                                                <small className="text-muted d-block mb-3">
                                                    {(pack.priceSek / pack.credits).toFixed(2)} kr/kredit
                                                </small>

                                                <Button
                                                    variant={index === 2 ? 'primary' : 'outline-primary'}
                                                    size="sm"
                                                    className="w-100"
                                                    disabled={!!checkoutLoading}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handlePurchase(pack.id);
                                                    }}
                                                >
                                                    {checkoutLoading === pack.id ? (
                                                        <Spinner animation="border" size="sm" />
                                                    ) : (
                                                        <>
                                                            <CreditCard size={14} className="me-1" />
                                                            Köp
                                                        </>
                                                    )}
                                                </Button>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <small className="text-muted me-auto">
                    Betalning hanteras säkert via Stripe
                </small>
                <Button variant="secondary" onClick={onHide}>
                    Avbryt
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default CreditPurchaseModal;
